import { useHistory, useParams } from 'react-router-dom';
import { useStyles } from './styles';
import agent from 'api/agent';
import { useEffect, useState } from 'react';
import { EditRestaurantGroupModel, IdName } from 'api/models';
import globalStore from 'store/globalStore';
import GroupFormFields, { CreateGroupForm, MenuOption } from './components/GroupFormFields';
import { Box, Typography, Button } from '@material-ui/core';
import { BackArrow, FormSkeleton } from 'components/shared';
import { Formik, FormikHelpers } from 'formik';
import React from 'react';
import editGroupValidationSchema from './editGroupValidationSchema';
import i18n from 'i18n-js';
import { useDispatch } from 'react-redux';
import { useFirstLoad } from 'hooks/useFirstLoad';

/* function normalize(values: CreateGroupForm) {
    return {
        name: values.name?.trim() ?? '',
        selectedRestaurants: (values.selectedRestaurants || [])
            .map((r) => r.id)
            .slice()
            .sort(),
        selectedUsers: (values.selectedUsers || [])
            .map((u) => u.id)
            .slice()
            .sort(),
    };
} */

export function EditRestaurantGroupPage() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { id: groupId } = useParams<{ id: string }>();
    const [restaurants, setRestaurants] = useState<IdName[]>([]);
    const [isGroupLoading, settleGroup] = useFirstLoad();
    const [initialValues, setInitialValues] = useState<CreateGroupForm>({
        name: '',
        selectedRestaurants: [],
        selectedUsers: [],
        menuOption: MenuOption.Empty,
        selectedMenus: [],
        catalogName: '',
    });

    const handleBackButtonClick = () => {
        history.goBack();
    };

    const handleEditGroup = (
        values: CreateGroupForm,
        formikHelpers: FormikHelpers<CreateGroupForm>
    ) => {
        const model: EditRestaurantGroupModel = {
            name: values.name.trim(),
            restaurants: values.selectedRestaurants.map((r) => r.id),
            managers: values.selectedUsers.map((u) => u.id),
        };

        agent.Restaurants.UpdateRestaurantGroup(groupId, model)
            .then(() => {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )
                );
                handleBackButtonClick();
            })
            .catch((e) => {
                dispatch(globalStore.actionCreators.showToaster('error', e));
            })
            .finally(() => {
                formikHelpers.setSubmitting(false);
            });
    };

    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            dispatch(globalStore.actionCreators.showSpiner());
            try {
                const [restaurantsResp, group] = await Promise.all([
                    agent.Restaurants.GetSimpleList(),
                    agent.Restaurants.GetRestaurantGroup(groupId),
                ]);

                if (!mounted) return;

                setRestaurants(restaurantsResp);
                setInitialValues({
                    name: group.name,
                    selectedRestaurants: group.restaurants,
                    selectedUsers: group.managers,
                    menuOption: MenuOption.Empty,
                    selectedMenus: [],
                    catalogName: group.catalogs[0]?.name || '',
                });
            } catch (e: any) {
                dispatch(globalStore.actionCreators.showToaster('error', e));
            } finally {
                if (mounted) {
                    settleGroup();
                    dispatch(globalStore.actionCreators.hideSpiner());
                }
            }
        };

        fetchData();
        return () => {
            mounted = false;
        };
    }, [dispatch, groupId]);

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={editGroupValidationSchema()}
                validateOnMount
                onSubmit={handleEditGroup}
            >
                {({ isValid, submitForm, dirty }) => {
                    return (
                        <>
                            <div className={classes.headerContainer}>
                                <div>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-start"
                                    >
                                        <BackArrow onClick={handleBackButtonClick} />
                                        <Typography className={classes.mLeft15}>
                                            {i18n.t('restaurant.backToRestaurants')}
                                        </Typography>
                                    </Box>
                                </div>
                                <div>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleBackButtonClick}
                                    >
                                        {i18n.t('button.cancel')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.mLeft15}
                                        onClick={submitForm}
                                        disabled={!dirty || !isValid}
                                    >
                                        {i18n.t('common.saveChanges')}
                                    </Button>
                                </div>
                            </div>
                            <div
                                className={classes.pageBodyContainer}
                                style={{ position: 'relative' }}
                            >
                                <Box className={classes.mBottom15}>
                                    <Typography variant="h6">
                                        {i18n.t('restaurant.editGroupPageTitle')}
                                    </Typography>
                                </Box>
                                <GroupFormFields restaurants={restaurants} isEditMode={true} />
                                <FormSkeleton isLoading={isGroupLoading} title columns={[3, 3]} />
                            </div>
                        </>
                    );
                }}
            </Formik>
        </>
    );
}
